import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { LifeBuoy, Send, CheckCircle2, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

export default function Help() {
  const [form, setForm] = useState({ guest_name: "", contact: "", message: "" });
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hostPhone, setHostPhone] = useState("");

  useEffect(() => {
    api.get("/property").then((r) => {
      const contacts = r.data?.contacts || [];
      const phone = (contacts.find((c) => /host/i.test(c.label)) || contacts[0] || {}).value || "";
      setHostPhone(phone);
    }).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.guest_name || !form.message) { toast.error("Please add your name and a message."); return; }
    setLoading(true);
    try {
      await api.post("/requests", { type: "help", ...form });
      setSent(true);
      toast.success("Your message was sent to the host!");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  if (sent) {
    return (
      <div className="max-w-xl mx-auto px-4 sm:px-8 py-20 text-center">
        <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}>
          <CheckCircle2 size={64} className="mx-auto text-brand-terracotta mb-6" />
          <h1 className="font-display text-4xl tracking-tight mb-3">Message Sent!</h1>
          <p className="text-brand-forest/70">Your host has been notified and will get back to you as soon as possible.</p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="max-w-xl mx-auto px-4 sm:px-8 py-8 sm:py-12">
      <div className="mb-8 text-center">
        <div className="h-14 w-14 mx-auto rounded-2xl bg-brand-sand flex items-center justify-center mb-4">
          <LifeBuoy size={28} className="text-brand-terracotta" />
        </div>
        <h1 className="font-display text-4xl sm:text-5xl tracking-tight">Need Help?</h1>
        <p className="text-brand-forest/60 mt-2">Here's your host's number, or send a quick message below.</p>
      </div>

      {hostPhone && (
        <div
          data-testid="help-call-host"
          className="mb-5 flex items-center gap-3 bg-brand-forest text-white rounded-3xl p-5 sm:p-6 shadow-sm"
        >
          <span className="h-11 w-11 rounded-2xl bg-white/15 flex items-center justify-center shrink-0">
            <Phone size={22} />
          </span>
          <span className="text-left">
            <span className="block text-xs uppercase tracking-widest text-white/60">Host Phone Number</span>
            <span className="block text-xl font-semibold" data-testid="help-host-phone">{hostPhone}</span>
          </span>
        </div>
      )}

      <form onSubmit={submit} className="bg-white rounded-3xl p-7 sm:p-8 border border-black/5 shadow-sm space-y-5" data-testid="help-form">
        <div>
          <Label className="mb-1.5 block">Your Name</Label>
          <Input data-testid="help-name" value={form.guest_name} onChange={(e) => setForm({ ...form, guest_name: e.target.value })} placeholder="e.g. Jamie" className="h-12 rounded-xl" />
        </div>
        <div>
          <Label className="mb-1.5 block">Phone or Email <span className="text-brand-forest/40">(so we can reply)</span></Label>
          <Input data-testid="help-contact" value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })} placeholder="you@email.com" className="h-12 rounded-xl" />
        </div>
        <div>
          <Label className="mb-1.5 block">How can we help?</Label>
          <Textarea data-testid="help-message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Let us know what you need…" rows={5} className="rounded-xl" />
        </div>
        <Button type="submit" disabled={loading} data-testid="help-submit" className="w-full h-12 rounded-full bg-brand-forest hover:bg-brand-terracotta text-white active:scale-95 transition-colors">
          <Send size={18} className="mr-2" /> {loading ? "Sending…" : "Send Message"}
        </Button>
      </form>
    </div>
  );
}
